/**
 * Seletor com busca do SERVIDOR — a UX do "Técnico responsável" (pedido da
 * PoolTrack, 02/10), que a PoolTrack pediu de novo para o "Responsável"
 * (05/10: "é a mesma UX da busca de técnico, é melhor"). Uma caixa de busca,
 * "Mostrar inativos", linhas com o nome e uma linha de baixo que distingue
 * homônimos, "Mostrando os N primeiros — refine a busca" e o "Sem …" para
 * limpar.
 *
 * O componente não sabe de onde vêm as linhas: quem o usa injeta `useResults`
 * (um hook de módulo, estável entre renders), que só consulta com o popover
 * aberto. O gatilho mostra o NOME do valor atual, que vem de quem chama — por
 * isso uma pessoa inativa já escolhida aparece certa mesmo com "Mostrar
 * inativos" desligado (ela não está na lista, mas está no valor).
 */
import { useState } from 'react';
import { ChevronDown, X, type LucideIcon } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Badge, Spinner } from '@/components/ui/misc';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { useDebouncedValue } from '@/features/tickets/use-debounced-value';

export interface SearchPickerValue {
  id: number;
  name: string;
}

export interface SearchPickerItem {
  id: number;
  name: string;
  active: boolean;
  /** a linha de baixo ("Poço Fundo · Sul de Minas", "Atendente · ana@…") */
  secondary?: string;
}

export interface SearchPickerResults {
  items: SearchPickerItem[];
  /** havia mais do que o servidor devolveu — a tela pede para refinar */
  hasMore: boolean;
  isPending: boolean;
  isError: boolean;
}

export type UseSearchPickerResults = (args: {
  search: string;
  includeInactive: boolean;
  /** só com o popover aberto */
  enabled: boolean;
}) => SearchPickerResults;

export interface SearchPickerLabels {
  /** aria-label do gatilho */
  aria: string;
  /** o gatilho sem valor: "Sem técnico" */
  none: string;
  /** a linha que limpa: "Sem técnico (limpar)" */
  clear: string;
  placeholder: string;
  /** busca sem resultado */
  notFound: string;
  /** lista vazia sem busca */
  empty: string;
  error: string;
}

export function SearchPicker({
  value,
  onChange,
  useResults,
  labels,
  icon: Icon,
  limit,
  disabled,
  compact = false,
  allowNone = true,
  meId,
}: {
  value: SearchPickerValue | null;
  /** o item vem junto para quem guarda o nome (formulário que ainda não salvou) */
  onChange: (id: number | null, item: SearchPickerItem | null) => void;
  useResults: UseSearchPickerResults;
  labels: SearchPickerLabels;
  icon: LucideIcon;
  /** o N de "Mostrando os N primeiros" */
  limit: number;
  disabled?: boolean;
  /** cabeçalho da visão compacta: gatilho baixo e estreito */
  compact?: boolean;
  /** oferece a linha "Sem … (limpar)" quando há valor */
  allowNone?: boolean;
  /** marca "(eu)" na linha de quem está usando */
  meId?: number;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);
  const debounced = useDebouncedValue(search, 300);
  const results = useResults({ search: debounced, includeInactive, enabled: open });
  const items = results.items;

  const choose = (item: SearchPickerItem | null) => {
    setOpen(false);
    setSearch('');
    const id = item?.id ?? null;
    if (id !== (value?.id ?? null)) onChange(id, item);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          aria-label={labels.aria}
          className={cn(
            'flex items-center justify-between gap-2 rounded-md border border-input bg-transparent text-left shadow-xs outline-none transition-[color,box-shadow]',
            'focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50',
            compact ? 'h-7 w-44 px-2 text-xs' : 'h-9 w-full px-3 text-sm'
          )}
        >
          <span
            className={cn('flex min-w-0 items-center gap-1.5', !value && 'text-muted-foreground')}
          >
            <Icon
              className={cn('shrink-0 text-muted-foreground', compact ? 'size-3.5' : 'size-4')}
              aria-hidden
            />
            <span className="truncate">
              {value ? (value.id === meId ? `${value.name} (eu)` : value.name) : labels.none}
            </span>
          </span>
          <ChevronDown className="size-4 shrink-0 text-muted-foreground" aria-hidden />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-96 max-w-[calc(100vw-2rem)] p-3">
        <div className="space-y-2 contain-[inline-size]">
          <Input
            autoFocus
            placeholder={labels.placeholder}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <label className="flex cursor-pointer items-center gap-2 text-xs text-muted-foreground">
            <Checkbox
              checked={includeInactive}
              onCheckedChange={(v) => setIncludeInactive(v === true)}
              aria-label="Mostrar inativos"
            />
            Mostrar inativos
          </label>
          <div className="max-h-64 overflow-y-auto rounded-md border">
            {value && allowNone ? (
              <button
                type="button"
                className="flex w-full cursor-pointer items-center gap-2 border-b px-3 py-2 text-left text-sm text-muted-foreground hover:bg-muted"
                onClick={() => choose(null)}
              >
                <X className="size-3.5" aria-hidden />
                {labels.clear}
              </button>
            ) : null}
            {results.isPending ? (
              <div className="flex justify-center p-3">
                <Spinner />
              </div>
            ) : results.isError ? (
              <p className="p-3 text-sm text-muted-foreground">{labels.error}</p>
            ) : items.length === 0 ? (
              <p className="p-3 text-sm text-muted-foreground">
                {debounced.trim() ? labels.notFound : labels.empty}
              </p>
            ) : (
              items.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  className={cn(
                    'block w-full cursor-pointer border-b px-3 py-2 text-left last:border-0 hover:bg-muted',
                    t.id === value?.id && 'bg-primary/5'
                  )}
                  onClick={() => choose(t)}
                >
                  <span className="flex items-center gap-2">
                    <span className="min-w-0 flex-1 truncate text-sm font-medium">
                      {t.id === meId ? `${t.name} (eu)` : t.name}
                    </span>
                    {!t.active ? <Badge>Inativo</Badge> : null}
                  </span>
                  {t.secondary ? (
                    <span className="block truncate text-xs text-muted-foreground">
                      {t.secondary}
                    </span>
                  ) : null}
                </button>
              ))
            )}
          </div>
          {results.hasMore ? (
            <p className="text-xs text-muted-foreground">
              Mostrando os {limit} primeiros — refine a busca.
            </p>
          ) : null}
        </div>
      </PopoverContent>
    </Popover>
  );
}
